import { Box, Flex, SimpleGrid, Stack, Text } from '@mantine/core';
import { Button as DesignButton } from 'src/design-system/Button';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { Select } from 'src/design-system/Select';
import { IconPencil } from '@tabler/icons-react';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { getInverseRelation, V2MemberRelationship } from './dataHelpers';
import { Relation } from '../hooks/useFamily';
import FamilyNameHeader from './FamilyNameHeader';

const RELATION_OPTIONS: { value: Relation; label: string }[] = [
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Partner', label: 'Partner' },
  { value: 'Parent', label: 'Parent' },
  { value: 'Child', label: 'Child' },
  { value: 'Sibling', label: 'Sibling' },
  { value: 'Other', label: 'Other' },
];

type Props = {
  selectedMembers: Client[];
  onBack: () => void;
  onCancel: () => void;
  onSubmit: (familyName: string, relationships: Record<string, V2MemberRelationship>) => void;
  isLoading: boolean;
  showAdvisorPicker?: boolean;
  advisorUserId?: string;
  onAdvisorChange?: (value: string | null) => void;
  initialFamilyName?: string;
  initialRelationships?: Record<string, V2MemberRelationship>;
  actionLabel?: string;
  title?: string;
  relationshipsEditable?: boolean;
};

export default function NameAndRelationshipsStep({
  selectedMembers,
  onBack,
  onCancel,
  onSubmit,
  isLoading,
  showAdvisorPicker,
  advisorUserId,
  onAdvisorChange,
  initialFamilyName = '',
  initialRelationships,
  actionLabel,
  title = 'Name this family and define relationships',
  relationshipsEditable = true,
}: Props) {
  const [familyName, setFamilyName] = useState(initialFamilyName);
  const [relationships, setRelationships] = useState<Record<string, V2MemberRelationship>>(() => {
    const grid: Record<string, V2MemberRelationship> = Object.fromEntries(
      selectedMembers.map(m => [
        m.id,
        Object.fromEntries(
          selectedMembers.filter(o => o.id !== m.id).map(o => [o.id, null as Relation | null]),
        ),
      ]),
    );
    if (initialRelationships) {
      for (const [fromId, toMap] of Object.entries(initialRelationships)) {
        for (const [toId, relation] of Object.entries(toMap)) {
          if (grid[fromId] && toId in grid[fromId]) grid[fromId][toId] = relation;
        }
      }
    }
    return grid;
  });

  const setRelation = (fromMemberId: string, toMemberId: string, relation: Relation | null) => {
    setRelationships(prev => {
      const next = {
        ...prev,
        [fromMemberId]: { ...prev[fromMemberId], [toMemberId]: relation },
      };
      if (relation !== null) {
        next[toMemberId] = { ...next[toMemberId], [fromMemberId]: getInverseRelation(relation) };
      }
      return next;
    });
  };

  const isValid = familyName.trim().length > 0;

  return (
    <Flex direction="column" h="100%">
      <FlowLayout
        header={
          <FamilyNameHeader
            title={title}
            familyName={familyName}
            onFamilyNameChange={setFamilyName}
            onCancel={onCancel}
            showAdvisorPicker={showAdvisorPicker}
            advisorUserId={advisorUserId}
            onAdvisorChange={onAdvisorChange}
          />
        }
        body={
          <FlowBody
            footerAlign="center"
            footer={
              <FlowNavigationFooter
                onBack={onBack}
                onAction={() => onSubmit(familyName, relationships)}
                actionDisabled={!isValid}
                actionLoading={isLoading}
                actionLabel={actionLabel}
              />
            }
          >
            <Box px="xs" pb="md">
              <SimpleGrid cols={2} spacing="sm">
                {selectedMembers.map(client => (
                  <Box key={client.id} bg="gray.1" p="md" style={{ borderRadius: 20 }}>
                    <Flex justify="space-between" align="flex-start" mb="md">
                      <Box>
                        <Text fw={FONT_WEIGHTS.semibold} size="md" c="gray.10">
                          {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
                        </Text>
                        {client.emailAddress && (
                          <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.7">
                            {client.emailAddress}
                          </Text>
                        )}
                      </Box>
                      <DesignButton
                        variant={client.advised ? 'tertiary' : 'secondary'}
                        size="sm"
                        rightSection={<IconPencil size={16} color="currentColor" />}
                        style={{ flexShrink: 0 }}
                      >
                        {client.advised ? 'Advised' : 'Not advised'}
                      </DesignButton>
                    </Flex>

                    <Stack gap="xs">
                      {selectedMembers
                        .filter(other => other.id !== client.id)
                        .map(other => (
                          <Flex key={other.id} align="center" gap="sm">
                            <Box style={{ flex: 1 }}>
                              {relationshipsEditable ? (
                                <Select
                                  placeholder="Select relationship"
                                  data={RELATION_OPTIONS}
                                  value={relationships[client.id]?.[other.id] ?? null}
                                  onChange={val =>
                                    setRelation(client.id, other.id, val as Relation | null)
                                  }
                                  variant="card"
                                />
                              ) : (
                                <Text size="md" fw={FONT_WEIGHTS.medium} c="gray.10">
                                  {relationships[client.id]?.[other.id] ?? '—'}
                                </Text>
                              )}
                            </Box>
                            <Text
                              size="sm"
                              fw={FONT_WEIGHTS.medium}
                              c="gray.7"
                              style={{ flexShrink: 0 }}
                            >
                              to {formatName(other.firstName, other.lastName, NameFormat.NATURAL)}
                            </Text>
                          </Flex>
                        ))}
                    </Stack>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </FlowBody>
        }
      />
    </Flex>
  );
}
